
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { Plus, Save, Trash2, Edit } from "lucide-react";
import { WellnessCard } from "@/components/WellnessCard";
import { StickyNote } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

interface Note {
  id: string;
  title: string;
  content: string;
  tags: string[];
  created_at: string;
  updated_at?: string;
  user_id?: string;
  type?: string;
}

// Type for notes in the database
interface DbNote {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  type: string;
}

export function NotesEditor() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [noteTitle, setNoteTitle] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [noteTags, setNoteTags] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const { user } = useAuth();
  
  const fetchNotes = async () => {
    try {
      const { data, error } = await supabase
        .from("user_notes")
        .select("*")
        .eq("type", "note") // Only fetch user notes, not Vita-generated content
        .order("created_at", { ascending: false });
      
      if (error) {
        throw error;
      }
      
      if (data) {
        // Transform database notes to our Note interface
        const transformedNotes = data.map((dbNote: DbNote) => {
          // Extract title and tags from content if available
          let title = "Untitled Note";
          let tags: string[] = [];
          
          try {
            // If content is JSON, try to parse it
            const parsedContent = JSON.parse(dbNote.content);
            if (parsedContent.title) title = parsedContent.title;
            if (parsedContent.tags) tags = parsedContent.tags;
            
            return {
              id: dbNote.id,
              title: parsedContent.title || title,
              content: parsedContent.content || dbNote.content,
              tags: parsedContent.tags || tags,
              created_at: dbNote.created_at,
              updated_at: dbNote.updated_at,
              user_id: dbNote.user_id,
              type: dbNote.type
            };
          } catch (e) {
            // If not JSON, use the content as is
            return {
              id: dbNote.id,
              title,
              content: dbNote.content,
              tags,
              created_at: dbNote.created_at,
              updated_at: dbNote.updated_at,
              user_id: dbNote.user_id,
              type: dbNote.type
            };
          }
        });
        
        setNotes(transformedNotes);
      }
    } catch (error) {
      console.error("Error fetching notes:", error);
      toast.error("Failed to load notes");
    }
  };
  
  useEffect(() => {
    fetchNotes();
  }, []);
  
  const handleSaveNote = async () => {
    if (!noteTitle.trim()) {
      toast.error("Please enter a note title");
      return;
    }
    
    if (!user) {
      toast.error("You must be logged in to save notes");
      return;
    }
    
    try {
      const tagsArray = noteTags.split(",")
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      
      // Store the structured note data as JSON in the content field
      const noteData = {
        title: noteTitle,
        content: noteContent,
        tags: tagsArray
      };
      
      if (isEditing && editingNoteId) {
        const { error } = await supabase
          .from("user_notes")
          .update({
            content: JSON.stringify(noteData),
            type: "note"
          })
          .eq("id", editingNoteId);
        
        if (error) throw error;
        toast.success("Note updated successfully");
      } else {
        const { error } = await supabase
          .from("user_notes")
          .insert({
            content: JSON.stringify(noteData),
            type: "note",
            user_id: user.id
          });
        
        if (error) throw error;
        toast.success("Note saved successfully");
      }
      
      // Reset form and refetch notes
      setNoteTitle("");
      setNoteContent("");
      setNoteTags("");
      setIsEditing(false);
      setEditingNoteId(null);
      fetchNotes();
    } catch (error) {
      console.error("Error saving note:", error);
      toast.error("Failed to save note");
    }
  };
  
  const handleEditNote = (note: Note) => {
    setNoteTitle(note.title);
    setNoteContent(note.content);
    setNoteTags(note.tags?.join(", ") || "");
    setIsEditing(true);
    setEditingNoteId(note.id);
  };
  
  const handleDeleteNote = async (id: string) => {
    try {
      const { error } = await supabase
        .from("user_notes")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
      
      toast.success("Note deleted successfully");
      fetchNotes();
    } catch (error) {
      console.error("Error deleting note:", error);
      toast.error("Failed to delete note");
    }
  };
  
  const handleViewNote = (note: Note) => {
    setSelectedNote(note);
    setIsNoteDialogOpen(true);
  };
  
  const resetForm = () => {
    setNoteTitle("");
    setNoteContent("");
    setNoteTags("");
    setIsEditing(false);
    setEditingNoteId(null);
  };
  
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  return (
    <div className="space-y-6">
      <WellnessCard 
        title="Personal Notes" 
        icon={<StickyNote className="h-5 w-5" />}
        category="notes"
      >
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{isEditing ? "Edit Note" : "Add New Note"}</CardTitle>
              <CardDescription>
                {isEditing 
                  ? "Update your existing note" 
                  : "Create a new personal note to track your wellness journey"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Input
                  placeholder="Note Title"
                  value={noteTitle}
                  onChange={(e) => setNoteTitle(e.target.value)}
                />
              </div>
              <div>
                <Textarea
                  placeholder="Write your note here..."
                  className="min-h-[150px]"
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                />
              </div>
              <div>
                <Input
                  placeholder="Tags (comma separated)"
                  value={noteTags}
                  onChange={(e) => setNoteTags(e.target.value)}
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              {isEditing ? (
                <>
                  <Button variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveNote}>
                    <Save className="mr-2 h-4 w-4" />
                    Update Note
                  </Button>
                </>
              ) : (
                <Button className="ml-auto" onClick={handleSaveNote}>
                  <Plus className="mr-2 h-4 w-4" />
                  Save Note
                </Button>
              )}
            </CardFooter>
          </Card>
          
          {notes.length > 0 ? (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Your Notes</h3>
              {notes.map((note) => (
                <Card key={note.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => handleViewNote(note)}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{note.title}</CardTitle>
                      <div className="flex space-x-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            handleEditNote(note); 
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            handleDeleteNote(note.id); 
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                    <CardDescription>{formatDate(note.created_at)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="line-clamp-2 text-sm text-muted-foreground">
                      {note.content}
                    </p>
                    {note.tags && note.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {note.tags.map((tag, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="bg-primary/10 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <StickyNote className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-medium">No Notes Yet</h3>
              <p className="text-muted-foreground mt-2 mb-4">
                Start creating notes to track your wellness journey!
              </p>
            </div>
          )}
        </div>
      </WellnessCard>
      
      <Dialog open={isNoteDialogOpen} onOpenChange={setIsNoteDialogOpen}>
        <DialogContent className="max-w-3xl">
          {selectedNote && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedNote.title}</DialogTitle>
                <DialogDescription>{formatDate(selectedNote.created_at)}</DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <div className="whitespace-pre-wrap">{selectedNote.content}</div>
                
                {selectedNote.tags && selectedNote.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-4">
                    {selectedNote.tags.map((tag, i) => (
                      <Badge key={i} variant="outline">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-between mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => setIsNoteDialogOpen(false)}
                >
                  Close
                </Button>
                <div className="space-x-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setIsNoteDialogOpen(false);
                      handleEditNote(selectedNote);
                    }}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => {
                      setIsNoteDialogOpen(false);
                      handleDeleteNote(selectedNote.id);
                    }}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
